import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as farFaHeart } from '@fortawesome/free-regular-svg-icons';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { saveWishlistItem } from 'Redux/ducks/wishlist';
import { updateAnonWishlistItem } from 'Redux/ducks/anon-wishlist';

import 'Components/Wishlist/WishlistButton.scss';

const WishlistButton = ({
  saveWishlistItem: _saveWishlistItem,
  slug,
  userId,
  authenticated,
  plainButton,
  bigButton,
  bundleButton,
  productId,
  wishlistLoaded,
  allSlugs,
  product,
  noText,
  reducedText,
  page,
  showMobileButton, // Variable for test 345 need addtocart button and wishlist button next to it
  className,
}) => {
  const active = allSlugs.includes(slug);

  const onClick = (e) => {
    e.preventDefault();
    if (!userId) return updateAnonWishlistItem({ name: product.name, componentType: 'Product Page - Wishlist Button', productId: product.product_id, slug });

    return _saveWishlistItem({ slug, name: product.name, active: !allSlugs.includes(slug), productId, componentType: 'Product Page - Wishlist Button', type: product.type });
  };

  if (page === 'product') {
    return (
      <div className="WishlistButton">
        <div className="WishlistButton__textContainer">
          <div className="WishlistButton__text">
            {active ? <FormattedMessage id="wishlistbutton.on-wishlist" defaultMessage="On Wishlist" /> : <FormattedMessage id="wishlistbutton.wishlist" defaultMessage="Wishlist" />}
          </div>
        </div>
        <button
          className={classNames('WishlistButton__button', { 'WishlistButton__button--active': active })}
          disabled={!wishlistLoaded && authenticated}
          aria-label={active ? 'On Wishlist' : 'Add to Wishlist'}
          onClick={onClick}
        >
          {active ? <FontAwesomeIcon icon={faHeart} title="On Wishlist" /> : <FontAwesomeIcon icon={farFaHeart} title="Add to Wishlist" />}
        </button>
      </div>
    );
  }

  if (plainButton) {
    return (
      <button className={className} disabled={!wishlistLoaded && authenticated} onClick={onClick}>
        {active ? <FormattedMessage id="wishlist.button.onwishlist" defaultMessage="On Wishlist" /> : <FormattedMessage id="wishlist.button.addtowishlist" defaultMessage="Add to Wishlist" />}
      </button>
    );
  }

  if (bigButton) {
    return (
      <button className="BigWishlistButton btn-primary" disabled={!wishlistLoaded && authenticated} onClick={onClick}>
        <div className="BigWishlistButton__icon">
          {active ? <FontAwesomeIcon icon={faHeart} /> : <FontAwesomeIcon icon={farFaHeart} />}
        </div>
        <div className="BigWishlistButton__text">
          {active ? <FormattedMessage id="wishlist.button.onwishlist" defaultMessage="On Wishlist" /> : <FormattedMessage id="wishlist.button.addtowishlist" defaultMessage="Add to Wishlist" />}
        </div>
      </button>
    );
  }

  if (bundleButton) {
    return (
      <div className="bundle-wishlist-btn-container">
        <Button
          aria-label={active ? 'On Wishlist' : 'Remind Me'}
          color="link"
          className={classNames('bundle-wishlist-btn', { 'in-wishlist': active })}
          disabled={!wishlistLoaded && authenticated}
          onClick={onClick}
        >
          <div className={classNames('btn btn-lg heart-container', { MobileProductWishlistButton: showMobileButton })}>
            {active ?
              <FontAwesomeIcon icon={faHeart} />
              :
              <FontAwesomeIcon icon={farFaHeart} />
            }
          </div>
          <div className="d-none d-md-block">
            <div className="text-container">
              {active ?
                <FormattedMessage id="wishlist.button.onwishlist" defaultMessage="On Wishlist" />
                :
                <React.Fragment>
                  {bundleButton &&
                    <FormattedMessage id="wishlist.Addtowishlist" defaultMessage="Remind Me" />
                  }
                </React.Fragment>
              }
            </div>
          </div>
        </Button>
      </div>
    );
  }

  return (
    <button
      className={classNames('wishlist-btn', { 'wishlist-btn-saved': true }, `${className}`)}
      disabled={!wishlistLoaded && authenticated}
      onClick={onClick}
    >
      {active ?
        <React.Fragment>
          {!noText && !reducedText && <FormattedMessage id="wishlist.button.onwishlist" defaultMessage="On Wishlist" />}
          {reducedText && !noText && <FormattedMessage id="wishlist.button.wishlist" defaultMessage="Wishlist" />}
          <FontAwesomeIcon icon={faHeart} />
        </React.Fragment>
        :
        <React.Fragment>
          {!noText && !reducedText && <FormattedMessage id="wishlist.button.addtowishlist" defaultMessage="Add to Wishlist" />}
          {reducedText && !noText && <FormattedMessage id="wishlist.button.wishlist" defaultMessage="Wishlist" />}

          <FontAwesomeIcon icon={farFaHeart} />
        </React.Fragment>
      }
    </button>
  );
};

WishlistButton.propTypes = {
  saveWishlistItem: PropTypes.func.isRequired,
  slug: PropTypes.string,
  userId: PropTypes.string,
  authenticated: PropTypes.bool,
  bigButton: PropTypes.bool,
  plainButton: PropTypes.bool,
  bundleButton: PropTypes.bool,
  productId: PropTypes.string,
  product: PropTypes.shape({}),
  wishlistLoaded: PropTypes.bool,
  allSlugs: PropTypes.arrayOf(PropTypes.string),
  noText: PropTypes.bool, // used by featured carousel to only show wishlist icon
  reducedText: PropTypes.bool, // used by featured carousel to show just wishlist instead of add to wishlist
  page: PropTypes.string, // used for the new product page design
  className: PropTypes.string,
};

WishlistButton.defaultProps = {
  slug: '',
  userId: '',
  authenticated: false,
  bigButton: false,
  plainButton: false,
  bundleButton: false,
  productId: '',
  product: {},
  wishlistLoaded: false,
  allSlugs: [],
  noText: false,
  reducedText: false,
  page: '',
  className: '',
};

const mapStateToProps = ({
  auth: { _id: userId, authenticated },
  wishlist: { wishlistLoaded, allSlugs },
}) => ({
  userId,
  authenticated,
  wishlistLoaded,
  allSlugs,
});

export default connect(mapStateToProps, { saveWishlistItem })(WishlistButton);
